import { Dialog, Button, Snackbar, SnackbarCloseReason, Alert } from '@mui/material';
import { useState } from 'react';
import { useTheme } from '../../../components/hooks/useTheme';
import colorConfigs from '../../../configs/colorConfigs';
import useAuth from '../../../components/hooks/useAuth';

type Props = {
  particularQuotes: any;
  showPopup: boolean;
  hidePopup: () => void;
  deleteQuote: (id: number) => void;
}

export const PaymentDetailsPopup = ({ particularQuotes, showPopup, hidePopup }: Props) => {
  const { theme }: any = useAuth();
  const { themeStyles } = useTheme();
  const [showCopyPublicLink, setShowCopuPublicLink] = useState<boolean>(false);
  const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setShowCopuPublicLink(false);
  };

  return (
    <>
      <Dialog
        open={showPopup}
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: '800px',
            width: '100%',
            borderRadius: '16px',
          },
        }}
      >
        <div className={`px-[24px] py-[10px] h-full ${themeStyles.main.tailwindSectionBg}`}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 320.591 320.591"
            className={`w-3 cursor-pointer float-right ${closeIconFill}`}
            onClick={() => hidePopup()}
          >
            <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
            <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
          </svg>
          <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
            <div className="w-full mt-2">
              <div className={`w-full`}>
                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                  <div className="flex items-center gap-1.5">
                    <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Payment Details - {particularQuotes?.id}</h2>
                  </div>
                </div>

                <div className="mt-2 p-2">

                  <div className="w-full">
                    <div className="grid grid-cols-3 gap-6">
                      {/* First Column */}
                      <div className="space-y-4">
                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Reservation</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.bookingNumber}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Customer Name</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.customerName}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Payment Type</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.paymentType}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Payment Method</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.paymentMethod}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Pickup Date</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.pickupDate}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Pickup Location</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.pickupLocationName}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Branch</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.branchName}</span>
                        </div>

                         <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Walk In Customer?</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.walkInCustomer}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Total Price</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.totalPrice}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Total Revenue</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.totalRevenue}</span>
                        </div>
                      </div>

                      {/* Second Column */}
                      <div className="space-y-4">
                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Return Date</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.returnDate}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Vehicle Class</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.vehicleClassName}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Payment Status</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.paymentStatus}</span>
                        </div>
                        
                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Status</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.status}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Local Address</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.localAddress}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Date</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.dateofPayment}</span>
                        </div>

                         <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Reference</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.reference}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Amount</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.amount}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Total Paid</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.totalPaid}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Outstanding Balance</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.outstandingBalance}</span>
                        </div>
                      </div>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-4 mt-6">
                      <Button variant="contained"
                        sx={{
                          backgroundColor: colorConfigs.btn.cancel.bg,
                          border: colorConfigs.btn.cancel.border,
                          color: colorConfigs.btn.cancel.color
                        }}
                        onClick={() => hidePopup()}
                      >
                        Close
                      </Button>
                    </div>

                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={showCopyPublicLink}
        autoHideDuration={1000}
        onClose={handleClose}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Data Copy to Clipboard.
        </Alert>
      </Snackbar>
    </>
  )
}